/* React hooks */
import {useEffect, useState, useCallback, useRef} from "react";
/* Routing */
import {useLocation, useParams} from "react-router-dom";
/* Contexts */
import useAuth from "../../hooks/useAuth";
import useDeceased from "../../hooks/useDeceased";
import {updateDeceasedPartnerInfos} from "../../api/pro/deceasedReq";
/* Components */
import useData from "../../hooks/useData";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import InputFullAddress from "../../components/common/input-layout/InputFullAddress";
import {safeJwtDecode} from "../../helper/jwtHelper";
import { normalizeZipCode } from "../../helper/tools";
import {
    checkRequiredFields,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput
} from "../../helper/tools";
import FormBirthInfos from "./FormBirthInfos";

export default function FormConjointInfos({
    legend,
    partnerId,
    partnerAddressId,
    partnerBirthPlaceId,
    setShowModal,
    data, 
    apiDeceasedInfos,
    apiPartnerInfos,
    apiCorrespondentInfos,
    apiAdminInfos,
    registerSubmitFunction,
    noSubmitButton = false,
}) {
    const { id } = useParams();
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const { selectedCity } = useData();
    const { deceasedInfos } = useDeceased();
    const [ values, setValues ] = useState(data);
    const [isFormModified, setIsFormModified] = useState(false);
    const fieldsetRef = useRef();
    // Sentinel distinct de tout id réel ou de `undefined`, pour détecter le tout premier chargement.
    const lastSyncedId = useRef('not-loaded-yet');
    // Les champs non contrôlés (defaultValue) ne se mettent pas à jour si `data` arrive après le
    // premier rendu (cas normal d'un fetch asynchrone) : ce compteur force leur remontage une fois
    // les données réellement chargées, pour qu'ils affichent la bonne valeur initiale.
    const [ dataLoadedKey, setDataLoadedKey ] = useState(0);

    useEffect(() => {
        // Resynchroniser seulement quand l'id du conjoint change réellement (premier chargement ou
        // changement de défunt) : un rechargement de `data` pour le MÊME conjoint, suite à
        // l'enregistrement réussi d'un autre sous-formulaire, ne doit pas écraser une saisie en cours.
        // (Se baser sur l'id plutôt que sur "une seule fois" évite le souci intermittent où `data`
        // contenait déjà une valeur partielle/obsolète au moment du tout premier rendu.)
        if (data && data?.id !== lastSyncedId.current) {
            setValues(data);
            setDataLoadedKey(k => k + 1);
            lastSyncedId.current = data?.id;
        }
    }, [ data ]);

    // Enregistrer la fonction de soumission auprès du formulaire parent
    useEffect(() => {
        if (registerSubmitFunction) {
            registerSubmitFunction(handleSubmit);
        }
    }, [registerSubmitFunction, values]);

    const onChange = (e) => {
        setIsFormModified(true);
        const newValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setValues(prevValues => ({...prevValues, [e.target.name]: newValue}));
        validateFormInput(e);
    };

    useEffect( () => {
        if (selectedCity?.city_zip) {
            let cleanCityName = '';
            let cleanZip = '';

            if (selectedCity?.city_zip && selectedCity.city_zip.includes('(')) {
                const match = selectedCity.city_zip.match(/^(.+?)\s*\((\d{5})\)$/);
                if (match) {
                    cleanCityName = match[1].trim();
                    cleanZip = match[2].trim();
                }
            }

            if (!cleanCityName && !cleanZip && selectedCity?.city_zip) {
                const parts = selectedCity.city_zip.split(' ');
                if (parts.length >= 2 && /^\d{5}$/.test(parts[0])) {
                    cleanZip = parts[0].trim();
                    cleanCityName = parts.slice(1).join(' ').trim();
                }
            }

            if (!cleanCityName && selectedCity?.name) {
                cleanCityName = selectedCity.name.trim();
            } else if (!cleanCityName && selectedCity?.city) {
                cleanCityName = selectedCity.city.trim();
            }

            if (!cleanZip && selectedCity?.zip) {
                cleanZip = selectedCity.zip.trim();
            } else if (!cleanZip && selectedCity?.zipcode) {
                cleanZip = selectedCity.zipcode.trim();
            }

            setValues(prevValues => ({
                ...prevValues,
                actualplace: {
                    ...prevValues?.actualplace,
                    zipcode: cleanZip,
                    city: cleanCityName
                }
            }));
        }
    }, [selectedCity?.city_zip])

    const onChangeActualPlace = (e) => {
        setIsFormModified(true);
        setValues(prevValues => {
            const updated = {
                ...prevValues,
                actualplace: {
                    ...prevValues?.actualplace,
                    [e.target.name]: e.target.value
                }
            };
            return updated;
        });
        validateFormInput(e);
    }

    const onChangeSameAddressAsDeceased = (e) => {
        const checked = e.target.checked;
        setIsFormModified(true);
        setValues(prevValues => ({
            ...prevValues,
            sameAddressAsDeceased: checked,
            actualplace: checked
                ? {
                    addressId: prevValues?.actualplace?.addressId || 0,
                    address: deceasedInfos?.actualplace?.address,
                    addressComp: deceasedInfos?.actualplace?.addressComp,
                    zipcode: deceasedInfos?.actualplace?.zipcode,
                    city: deceasedInfos?.actualplace?.city,
                    country: deceasedInfos?.actualplace?.country || "FR"
                }
                : prevValues?.actualplace
        }));
    };

    useEffect(() => {
        // Le champ adresse peut avoir été marqué "invalid" lors d'une précédente tentative de
        // validation ; le pré-remplissage automatique via la case à cocher ne déclenche aucun
        // événement sur le champ (désactivé), donc cette classe ne se retire pas d'elle-même.
        if (values?.sameAddressAsDeceased && fieldsetRef.current) {
            const addressFields = fieldsetRef.current.querySelectorAll('[name="address"], [name="addressComp"], [name="zipcode"], [name="city"], [name="country"]');
            addressFields.forEach(field => {
                field.parentElement.classList.remove("invalid");
                field.parentElement.classList.add("valid");
            });
        }
    }, [ values?.sameAddressAsDeceased, values?.actualplace ]);

    const handleRemoveModal = () => {
        setShowModal(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Utiliser fieldsetRef pour la validation, permettant l'appel depuis le parent
        const formElement = fieldsetRef.current || e.target;
        if (!checkRequiredFields(formElement)) {
            return;
        }

        let body = {
            deceasedId: id,
            agencyId: agencyId,
            birthplace: {
                addressId: partnerBirthPlaceId || 0,
                address: values?.birthplace?.address,
                addressComp: values?.birthplace?.addressComp,
                city: values?.birthplace?.city,
                zipcode: normalizeZipCode(values?.birthplace?.zipcode),
                country: values?.birthplace?.country ? values?.birthplace?.country : "FR"
            },
            lastName: values?.lastName,
            firstName: values?.firstName,
            birthName: values?.birthName ? values?.birthName : "",
            actualplace: {
                addressId: values?.actualplace?.addressId || 0,
                address: values?.actualplace?.address,
                addressComp: values?.actualplace?.addressComp,
                zipcode: normalizeZipCode(values?.actualplace?.zipcode),
                city: values?.actualplace?.city,
                country: values?.actualplace?.country ? values?.actualplace?.country : "FR"
            },
            email: values?.email,
            phone: values?.phone,
            mobile: values?.mobile,
            partnerId: partnerId ? partnerId : "0",
            addressId: partnerAddressId,
            title: values?.title,
            civility: values?.civility || "M",
            birthDate: values?.birthDate,
        }

        await updateDeceasedPartnerInfos(token, body)
            .then((response) => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    setIsFormModified(false);

                    // Afficher le message de succès dans le bon fieldset
                    const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                    if (errorDiv) {
                        errorDiv.insertAdjacentHTML('beforeend', '<p class="success-message">Les modifications ont été enregistrées avec succès !</p>');

                        // Supprimer le message après 3 secondes
                        setTimeout(() => {
                            const successMsg = errorDiv.querySelector('.success-message');
                            if (successMsg) {
                                successMsg.remove();
                            }
                        }, 3000);
                    }

                    apiPartnerInfos()
                    apiDeceasedInfos()
                    apiCorrespondentInfos()
                    apiAdminInfos()

                } else {
                    getErrorListFromValidation(response?.response?.data?.error);
                }
            })
    }
        
    const FormWrapper = noSubmitButton ? 'div' : 'form';
    const formProps = noSubmitButton ? {} : { onSubmit: handleSubmit, method: "POST", noValidate: true };

    return (
        <FormWrapper {...formProps}>
            <fieldset ref={fieldsetRef}>
                {(legend !== "no-legend") && <legend>{legend}</legend>}
                <div className="form-group">
                    <CivilityForm required="required" onChange={onChange} value={values?.civility} />
                    {legend === "Informations du défunt" &&
                        <div className="form-input">
                            <label htmlFor="hiddenCivility">Masquer la civilité</label>
                            <input
                                type="checkbox"
                                name="hiddenCivility"
                                defaultChecked={values?.hiddenCivility}
                                onChange={onChange}
                            />
                        </div>
                    }
                </div>

                <LastNameForm key={`lastName-${dataLoadedKey}`} required={true} onChange={onChange} value={values?.lastName}/>
                <div className="form-input">
                    <label htmlFor="birthName">Nom de naissance </label>
                    <input
                        key={`birthName-${dataLoadedKey}`}
                        placeholder="Nom de naissance"
                        type="text"
                        name="birthName"
                        defaultValue={values?.birthName}
                        pattern={patternListChoices?.lastname}
                        onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChange(e); }}/>
                </div>
                <FirstnameForm key={`firstName-${dataLoadedKey}`} required={true} onChange={onChange} value={values?.firstName}/>
                <div className="form-input">
                    <label htmlFor="sameAddressAsDeceased">L'adresse est identique à celle du défunt</label>
                    <input
                        type="checkbox"
                        name="sameAddressAsDeceased"
                        id="sameAddressAsDeceased"
                        checked={!!values?.sameAddressAsDeceased}
                        onChange={onChangeSameAddressAsDeceased}
                    />
                </div>
                <div className="form-row">
                    <InputFullAddress
                        countryField="country"
                        zipField="zipcode"
                        cityField="city"
                        addressField="address"
                        compField="addressComp"
                        address={values?.actualplace?.address}
                        comp={values?.actualplace?.addressComp}
                        city={values?.actualplace?.city}
                        zipcode={values?.actualplace?.zipcode}
                        country={values?.actualplace?.country || "FR"}
                        required={!values?.sameAddressAsDeceased}
                        disabled={!!values?.sameAddressAsDeceased}
                        onChange={onChangeActualPlace}
                        type="actual"
                    />
                </div>
                {legend?.includes("Informations du conjoint") &&
                    <>
                        <div className="form-input">
                            <label htmlFor="phone">Téléphone fixe</label>
                            <input
                                pattern={patternListChoices?.phone}
                                placeholder="Téléphone fixe"
                                type="text"
                                name="phone"
                                value={values?.phone}
                                onChange={onChange}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="mobile">Téléphone mobile</label>
                            <input
                                pattern={patternListChoices?.phone}
                                placeholder="Téléphone mobile"
                                type="text"
                                name="mobile"
                                value={values?.mobile}
                                onChange={onChange}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="email">Email</label>
                            <input
                                pattern={patternListChoices?.email}
                                placeholder="Email"
                                type="email"
                                name="email"
                                value={values?.email}
                                onChange={onChange}/>
                        </div>
                    </>
                }
                <div className="errors"></div>
                </fieldset>
                {!legend?.includes("Informations du conjoint") &&
                    <FormBirthInfos
                        fullForm={values}
                        setFullForm={setValues}
                        legendText="Informations de naissance du conjoint"
                        dateRequired={false}
                    />
                }
                {!noSubmitButton && (
                    <div className="buttons-container">
                        <button
                            type="button"
                            className="green-button"
                            onClick={handleSubmit}
                            disabled={!isFormModified}
                        >
                            <i className="fa fa-check"></i>
                            <span>Enregistrer les modifications</span>
                        </button>
                    </div>
                )}
        </FormWrapper>
    )
}